import styled from "styled-components";
import {fontFamilies, fontWeights, radii} from "theme/tokens";

/**
 * The primitives both statutory declaration dialogs share (kitecorp/transferify-ui#3698): the account's
 * "Cancel and get a refund" (`account/withdrawal/WithdrawalSubmissionDialog`) and an advertising booking's
 * withdrawal (`sponsor/SponsorBookingWithdrawal`). One copy, so the two dialogs' fields, failures, actions
 * and confirmation title cannot drift apart; what differs between them stays in each one's own styles.
 */

export const Field = styled.div`
	margin-top: 18px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

export const Failure = styled.div`
	margin-top: 14px;
	padding: 12px 14px;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	font-size: 13.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/**
 * The row of a declaration's two actions. Wraps when a long label (Romanian, German) would not fit beside
 * the other one, and stacks on a phone with the confirming action on top.
 */
export const DialogActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 10px;
	padding: 20px 24px 24px;

	button {
		min-height: 44px;
		height: auto;
	}

	@media (max-width: 480px) {
		align-items: stretch;
		flex-direction: column-reverse;

		button {
			width: 100%;
		}
	}
`;

/** Form submit action; unlike AccountCards.PrimaryButton, its native submit type is not pinned away. A long label
 * wraps inside it rather than overflowing a fixed height. */
export const SubmitButton = styled.button`
	min-height: 40px;
	padding: 8px 16px;
	border: none;
	border-radius: ${radii.control};
	font-family: ${fontFamilies.sans};
	font-size: 14px;
	font-weight: 700;
	cursor: pointer;
	transition: 0.16s;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	&:hover:not(:disabled):not([aria-disabled="true"]) {
		background: ${({theme}) => theme.tokens.actionFillHover};
	}

	/* A send in flight marks the button aria-disabled rather than disabled, so it keeps keyboard focus
	   (kitecorp/transferify-ui#3079). It looks exactly as unavailable as a disabled one. */
	&:disabled,
	&[aria-disabled="true"] {
		cursor: not-allowed;
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
	}
`;

export const SuccessTitle = styled.h3`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(28px, 5.5vw, 34px);
	line-height: 1.05;
	color: ${({theme}) => theme.tokens.ink};
`;
